import { useState, useMemo } from 'react';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Button } from '@vnetwork/ui/primitives';
import { useMyReferralConversions } from '@/api/account';
import { paginationView } from '@/pages/customers/pagination-summary';
import { buildConversionColumns } from '@/pages/affiliates/detail/conversions-tab';

const PAGE_SIZE = 20;

/**
 * Employee self-view: Referral signups tab.
 * Reuses column definitions from the manager conversions-tab; data comes from the
 * `/employees/current/referral-conversions` endpoint instead.
 */
export function ReferralConversionsTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useMyReferralConversions({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildConversionColumns(), []);

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load referral signups"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <DataTable
      columns={columns}
      data={rows}
      enableSorting={false}
      getRowId={(r) => String(r.click_id)}
      emptyState={
        <EmptyState
          title="No referral signups yet"
          description="Customers who signed up via your referral link will appear here."
        />
      }
      footer={
        isLoading ? null : (
          <Pagination
            page={page}
            pageCount={view.pageCount}
            onPageChange={setPage}
            summary={view.summary}
          />
        )
      }
    />
  );
}
